import React, { useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { fetchAPI } from "../utils/fetchAPI";

interface AddWasteModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  parentItem: {
    id: number;
    name: string;
    steelType: number;
    steelStatus: number;
    width: number;
    height: number;
    thickness: number;
    weight: number;
    grade: string;
    usedTo: string;
    info: string;
  };
}

const AddWasteModal: React.FC<AddWasteModalProps> = ({ show, onHide, onSuccess, parentItem }) => {
  const [formData, setFormData] = useState({
    width: "",
    height: "",
    weight: "",
    usedTo: parentItem.usedTo || "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      // First, update the parent item to mark it as used
      const updateParentResponse = await fetchAPI("POST", "updateSteel.php", {
        id: parentItem.id,
        name: parentItem.name,
        steelType: 3, // Wykorzystana
        steelStatus: parentItem.steelStatus,
        width: parentItem.width,
        height: parentItem.height,
        thickness: parentItem.thickness,
        weight: parentItem.weight,
        grade: parentItem.grade,
        usedTo: formData.usedTo, // Używamy wartości z formularza
        info: parentItem.info,
      });

      if (!updateParentResponse.success) {
        throw new Error(updateParentResponse.message || "Błąd podczas aktualizacji oryginalnej stali");
      }

      // Then, add the new waste item
      const addWasteResponse = await fetchAPI("POST", "addSteel.php", {
        name: `Odpad z ${parentItem.name}`,
        steelType: 2, // Odpad użyteczny
        steelStatus: 0, // Dostępna
        width: Number(formData.width),
        height: Number(formData.height),
        thickness: parentItem.thickness, // Używamy grubości z oryginalnej pozycji
        weight: Number(formData.weight),
        grade: parentItem.grade,
        usedTo: `Odpad z ${parentItem.name} (ID: ${parentItem.id})`,
        info: `Utworzony z: ${parentItem.name} (ID: ${parentItem.id})`,
      });

      if (!addWasteResponse.success) {
        throw new Error(addWasteResponse.message || "Błąd podczas dodawania odpadu");
      }

      onSuccess();
      onHide();
    } catch (error: any) {
      setError(error.message || "Wystąpił błąd podczas operacji");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Dodaj odpad</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Szerokość (mm)</Form.Label>
            <Form.Control
              type="number"
              name="width"
              value={formData.width}
              onChange={handleChange}
              required
              min="0"
              step="0.1"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Długość (mm)</Form.Label>
            <Form.Control
              type="number"
              name="height"
              value={formData.height}
              onChange={handleChange}
              required
              min="0"
              step="0.1"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Waga (kg)</Form.Label>
            <Form.Control
              type="number"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              required
              min="0"
              step="0.1"
            />
          </Form.Group>
          <hr className="my-4" />
          <Form.Group className="mb-3">
            <Form.Label>Do czego zużyto oryginał</Form.Label>
            <Form.Control type="text" name="usedTo" value={formData.usedTo} onChange={handleChange} required />
          </Form.Group>
          {error && <div className="text-danger mb-3">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? "Dodawanie..." : "Dodaj odpad"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddWasteModal;
